import React from 'react';
import styled from 'styled-components';
import classNames from 'classnames';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import Button, { SquareButton } from '../../button';
import { Div, Flex } from '../../box';
import Panel from '../../panel';
import { PopoverContentContainer } from '../../popover';
import { H3 } from '../../title';
import { colors } from '../../../style';
import Text from '../../text';
import Avatar from '../../avatar';
import Icon from '../../icon';

// constants
import materialIcons from '../../icon/constants/material-icons-constants';
import { ButtonProps } from '../../button/button';
import Link from '../../link';

const UserMenu = ({ children }: { children?: React.ReactNode }) => {
  return <DropdownMenu.Root modal={false}>{children}</DropdownMenu.Root>;
};

const CONTAINER_WIDTH = 304;

UserMenu.Trigger = ({
  name,
  profileImage,
  userId,
  'data-testid': dataTestId,
}: {
  name?: string;
  profileImage?: string;
  userId?: number;
  'data-testid'?: string;
}) => {
  return (
    <DropdownMenu.Trigger asChild>
      <SquareButton
        variant={'secondary-clean'}
        aria-label={'user menu'}
        className={'avatar-button shared-button hidden-xs'}
        data-testid={dataTestId}
      >
        <Avatar name={name} photo={profileImage} size={'xs'} userId={userId} />
      </SquareButton>
    </DropdownMenu.Trigger>
  );
};

const StyledDropdownMenuContent = styled(DropdownMenu.Content)`
  opacity: 1;
  animation: fadeInFromNone 0.15s ease;
  @keyframes fadeInFromNone {
    from {
      opacity: 0;
    }

    to {
      opacity: 1;
    }
  }
`;
UserMenu.PopoverContent = ({ children }: { children?: React.ReactNode }) => {
  return (
    <StyledDropdownMenuContent style={{ zIndex: 9999 }} sideOffset={8} collisionPadding={{ right: 24 }}>
      <PopoverContentContainer padding={'none'} width={CONTAINER_WIDTH}>
        {children}
      </PopoverContentContainer>
    </StyledDropdownMenuContent>
  );
};

const UserMenuSectionStripe = styled(Div)`
  width: ${CONTAINER_WIDTH}px;
  height: 8px;
  background: ${colors.lightPumpkin};
`;

/* USER MENU PROFILE SECTION */

UserMenu.MenuProfileSection = ({
  name,
  profileImage,
  title,
  children,
  userId,
}: {
  name?: string;
  profileImage?: string;
  userId?: number;
  title?: string;
  children: React.ReactNode;
}) => {
  return (
    <>
      <Panel
        height={100}
        flexGrow={1}
        display={'flex'}
        flexDirection={'column'}
        marginBottom={'md'}
        alignItems={'center'}
        position={'relative'}
        variant={'light-blue'}
        justifyContent={'flex-end'}
      >
        <Div position={'absolute'} top={'50%'} style={{ border: '2px solid white' }} borderRadius={'round'}>
          <Avatar photo={profileImage} name={name} size={'xl'} aria-hidden userId={userId} />
        </Div>
        <UserMenuSectionStripe />
      </Panel>
      <Flex alignItems={'center'} flexDirection={'column'}>
                {name && (
          <DropdownMenu.Label asChild>
            <H3 marginBottom={'none'} paddingTop={'md'} paddingHorizontal={'xs'} align={'center'} data-testid={'user-menu-full-name'}>
              {name}
            </H3>
          </DropdownMenu.Label>
        )}
        {title && (
          <DropdownMenu.Label asChild>
            <Text variant={'gray600'} padding={'xs'} align={'center'} data-testid={'user-menu-role'}>
              {title}
            </Text>
          </DropdownMenu.Label>
        )}
        {children}
      </Flex>
    </>
  );
};

UserMenu.SeeProfileLink = ({
  href,
  to,
  label,
  'data-testid': dataTestId,
}: {
  href?: string;
  to?: string;
  label: string;
  'data-testid'?: string;
}) => {
  return (
    <DropdownMenu.Item asChild>
      <Button
        componentClass={href ? 'a' : to ? Link : undefined}
        to={to}
        href={href}
        variant={'action-ghost'}
        size={'sm'}
        style={{ textDecoration: 'none' }}
        data-testid={dataTestId}
      >
        {label}
      </Button>
    </DropdownMenu.Item>
  );
};

/* USER MENU LINKS */
interface UserMenuLinkButtonProps extends ButtonProps {
  className?: string;
  to?: string;
  children?: React.ReactNode | string;
  href?: string;
  componentClass?: React.ElementType;
  disabled?: boolean;
  exact?: boolean;
  ref?: React.Ref<any>;
}

const StyledUserMenuLinkButton = styled.div<UserMenuLinkButtonProps>`
  display: flex;
  outline: none;
  gap: 8px;
  padding: 8px;
  color: ${colors.gray900};
  text-decoration: none;
  font-size: 1rem;
  justify-content: flex-start;
  border: 1px solid transparent;
  align-items: center;
  border-radius: 4px;
  font-weight: 400;
  background: transparent;

  &:hover:not([disabled]),
  &:focus-visible,
  &:active {
    background: ${colors.gray100};
  }

  &:not([disabled]) {
    cursor: pointer;
  }

  &[disabled] {
    cursor: not-allowed;
    background: ${colors.gray400};
    color: ${colors.gray900};
  }
`;

UserMenu.UserMenuLinkItem = ({
  href,
  to,
  label,
  iconName,
  onClick,
  target,
  'data-testid': dataTestId,
}: {
  href?: string;
  to?: string;
  label: string;
  target?: '_blank';
  onClick?: (e: React.MouseEvent<HTMLElement>) => void;
  iconName: keyof typeof materialIcons;
  'data-testid'?: string;
}) => {
  return (
    <DropdownMenu.Item asChild>
      <UserMenuLinkButton href={href} to={to} onClick={onClick} target={target} data-testid={dataTestId}>
        <Icon name={iconName} outlined />
        <DropdownMenu.Label>{label}</DropdownMenu.Label>
      </UserMenuLinkButton>
    </DropdownMenu.Item>
  );
};

const UserMenuLinkButton = ({
  className,
  to,
  href,
  children,
  ...rest
}: Pick<UserMenuLinkButtonProps, 'className' | 'to' | 'href' | 'children' | 'onClick' | 'target'> & {
  'data-testid'?: string;
}) => {
  return (
    <StyledUserMenuLinkButton
      className={classNames('navbar-button', className)}
      as={href ? 'a' : to ? Link : undefined}
      to={to}
      href={href}
      {...rest}
    >
      {children}
    </StyledUserMenuLinkButton>
  );
};

UserMenu.MenuLinksContainer = ({ children }: { children: React.ReactNode }) => {
  return (
    <Flex maxHeight={200} overflowY={'auto'} flexDirection={'column'} marginTop={'xs'} padding={'sm'}>
      {children}
    </Flex>
  );
};

export default UserMenu;
